// WAHR monthly-workbook parser — the historical-backfill source for the WAHR
// Comparison page. Each monthly .xlsx (one per ~4-week WAHR period) carries three
// tabs we care about:
//
//   • "…Activity Summar…"  — one row per DRIVER × DAY. Holds the two hours bases:
//         Portal Time  (true working hours, generally longer)  and
//         Scanner Time (handheld-device time, generally shorter),
//     plus onboard/delivered reference counts. Column headers are ambiguous
//     ("Total time" appears twice), so we anchor the hours columns off the
//     unambiguous "Parcels Per hour" columns: hours = (PPH column − 1).
//
//   • "Data"               — the WAHR raw scan feed. TRUE parcel volume is the
//     pre-summed total in COLUMN J (index 9), per driver × day. We do NOT re-sum
//     A–E/U; Column J is authoritative (confirmed by Paul, 27 Jul 2026).
//
//   • "Totals"             — a hand-built year-over-year sheet. Derived, not
//     imported; the numbers here are rebuilt from Data + Activity Summary.
//
// Tab names vary between files ("Delivery Driver Activity Summar" vs "Driver
// Delivery Activity Summar", "Data" vs "Data (WAHR)"), so every tab and column is
// matched fuzzily by name with a hard-coded index fallback — the same defensive
// pattern as RouteSectionsEngine.parseAuditWorkbook, because AP reorders columns.
//
// Dates are read as raw Excel serials and converted with a pure-UTC formula so the
// result never drifts with the machine timezone. Logins are upper-cased for
// identity (files mix "AGUEROOTH" and "anduncesr").
import * as XLSX from 'xlsx';

const up = (s) => (s == null ? '' : String(s)).trim().toUpperCase();
const num = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// Excel serial (days since 1899-12-30) → 'YYYY-MM-DD', timezone-independent.
// 25569 = the serial for 1970-01-01, so (serial − 25569) days is UTC epoch days.
function serialToISO(n) {
  const ms = Math.round((n - 25569) * 86400 * 1000);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

const isoOf = (y, mo, d) => `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

// Swap the month and day of an ISO date, or '' if the result isn't a real date.
function swapMonthDay(iso) {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const y = m[1]; const mo = +m[3]; const d = +m[2]; // month<->day swapped
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return '';
  return isoOf(y, mo, d);
}

// Coerce whatever a date cell holds (raw serial / Date / string) to ISO.
// `inWindow` (optional) is a predicate ISO→bool for the file's known period. AP
// mixes AU (D/M/Y) and US (M/D/Y) dates, and some cells were even SAVED with the
// wrong serial (e.g. "1 Aug" keyed US-style became the Jan-8 serial). So after a
// base reading, if it lands OUTSIDE the file's window but swapping month↔day lands
// INSIDE, take the swap — it was a transposed date. Serials/Dates already inside
// the window are trusted untouched.
export function cellToISO(v, inWindow) {
  if (v == null || v === '') return '';
  let base = '';
  if (typeof v === 'number') base = serialToISO(v);
  else if (v instanceof Date) base = isoOf(v.getFullYear(), v.getMonth() + 1, v.getDate());
  else {
    const s = String(v).trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); // ISO
    if (m) base = `${m[1]}-${m[2]}-${m[3]}`;
    else {
      m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/); // p1/p2/YYYY (assume AU D/M first)
      if (m) base = isoOf(m[3], +m[2] <= 12 ? +m[2] : +m[1], +m[2] <= 12 ? +m[1] : +m[2]);
    }
  }
  if (!base) return '';
  if (inWindow && !inWindow(base)) {
    const sw = swapMonthDay(base);
    if (sw && inWindow(sw)) return sw;
  }
  return base;
}

// Find a sheet whose name matches any of the regexes, in priority order.
function findSheet(wb, regexes) {
  for (const re of regexes) {
    const hit = wb.SheetNames.find((n) => re.test(n));
    if (hit) return hit;
  }
  return null;
}

// Column index whose header (row 0) matches `re`; -1 if none.
function colByHeader(header, re) {
  for (let i = 0; i < header.length; i += 1) {
    if (re.test(String(header[i] || '').trim())) return i;
  }
  return -1;
}
// All column indices whose header matches `re` (for the repeated PPH columns).
function colsByHeader(header, re) {
  const out = [];
  for (let i = 0; i < header.length; i += 1) {
    if (re.test(String(header[i] || '').trim())) out.push(i);
  }
  return out;
}

const addISO = (iso, n) => { const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

// Find the file's real period as the densest ≤6-week window of parcel volume.
// Robust to sparse outliers (backdated adjustments, a stray future date) and to
// partial hours tabs — the bulk of a month's parcels forms one tight cluster.
// `weighted` = [{iso, weight}]. Returns { lo, hi } (padded) or null.
function densestWindow(weighted) {
  if (!weighted.length) return null;
  const w = {};
  weighted.forEach((x) => { if (x.iso) w[x.iso] = (w[x.iso] || 0) + (x.weight || 1); });
  const days = Object.keys(w).sort();
  if (!days.length) return null;
  const SPAN = 41; // up to 6 delivery weeks (covers the Mon–Sat peak period)
  let best = days[0]; let bestSum = -1;
  for (const s of days) {
    const hi = addISO(s, SPAN);
    let sum = 0;
    for (const d of days) { if (d >= s && d <= hi) sum += w[d]; }
    if (sum > bestSum) { bestSum = sum; best = s; }
  }
  return { lo: addISO(best, -3), hi: addISO(best, SPAN + 3) };
}

// Parse the hand-maintained "Totals" tab — the authoritative daily depot totals
// and the file's true period. Layout: a date column (Excel serials) immediately
// followed by a daily-total column; older files have TWO such pairs (prior year
// then current year, e.g. 2023 | … | 2024). We take the pair whose dates are the
// LATEST (the file's current year). Returns { daily:{iso:total}, minDate, maxDate,
// grand } or null. Used to window + validate the Data-tab parcel volumes.
export function parseTotalsTab(wb) {
  const name = findSheet(wb, [/^\s*totals?\s*$/i]);
  if (!name) return null;
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, blankrows: false, defval: '' });
  if (!rows.length) return null;
  const ncol = rows.reduce((m, r) => Math.max(m, r.length), 0);
  const isSerial = (v) => typeof v === 'number' && v >= 40000 && v <= 60000; // ~2009–2064
  let best = null;
  for (let c = 0; c < ncol; c += 1) {
    let cnt = 0;
    for (const r of rows) if (isSerial(r[c])) cnt += 1;
    if (cnt < 5) continue; // not a date column
    const daily = {}; let mn = null; let mx = null; let grand = 0;
    for (const r of rows) {
      if (!isSerial(r[c])) continue;
      const iso = serialToISO(r[c]);
      const t = num(r[c + 1]);
      daily[iso] = (daily[iso] || 0) + t; grand += t;
      if (!mn || iso < mn) mn = iso;
      if (!mx || iso > mx) mx = iso;
    }
    if (Object.keys(daily).length && (!best || mx > best.maxDate)) best = { daily, minDate: mn, maxDate: mx, grand };
  }
  return best;
}

// Pull raw (dateCell, total) pairs from the Data tab for window detection.
function extractDataDates(wb) {
  const name = findSheet(wb, [/^data\b/i, /data\s*\(?\s*wahr/i, /^data/i]);
  if (!name) return [];
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, blankrows: false, defval: '' });
  if (!rows.length) return [];
  const header = rows[0];
  const dateCol = (() => { const c = colByHeader(header, /event\s*date/i); return c >= 0 ? c : 0; })();
  const totalCol = (() => { const tt = colByHeader(header, /trans\.?\s*type/i); return tt > 0 ? tt - 1 : 9; })();
  const out = [];
  for (let i = 1; i < rows.length; i += 1) {
    const r = rows[i]; if (!r) continue;
    const iso = cellToISO(r[dateCol]); // AU default, no clamp yet
    if (iso) out.push({ iso, weight: Math.max(1, num(r[totalCol])) });
  }
  return out;
}

// ── Data tab → parcels per driver × day (Column J total) ─────────────────────
// `inWindow` is the file's period predicate (from the hours-summary tab). It both
// fixes AU/US slash dates (via cellToISO) AND drops rows that fall outside the
// period — older Data tabs carry backdated "Adjustment"/"WAHR Auto" correction
// rows (e.g. a December file with a row dated 12 Feb) that would otherwise stretch
// the span and inflate totals. Dropped rows are counted and reported, never merged.
function parseDataTab(wb, warnings, inWindow) {
  const name = findSheet(wb, [/^data\b/i, /data\s*\(?\s*wahr/i, /^data/i]);
  if (!name) { warnings.push('No "Data" (WAHR) tab found — parcel volume will be 0.'); return { parcels: {}, droppedRows: 0, droppedParcels: 0 }; }
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, blankrows: false, defval: '' });
  if (!rows.length) return { parcels: {}, droppedRows: 0, droppedParcels: 0 };
  const header = rows[0];
  const dateCol = (() => { const c = colByHeader(header, /event\s*date/i); return c >= 0 ? c : 0; })();
  const driverCol = (() => { const c = colByHeader(header, /driver\s*name/i); return c >= 0 ? c : 13; })();
  // Column J = the pre-summed total. Prefer the column immediately before
  // "Trans.Type" (that's J across every sample), else the literal J (index 9).
  const totalCol = (() => {
    const tt = colByHeader(header, /trans\.?\s*type/i);
    return tt > 0 ? tt - 1 : 9;
  })();

  const parcels = {}; // key `${iso}|${LOGIN}` → parcels
  let droppedRows = 0; let droppedParcels = 0;
  for (let i = 1; i < rows.length; i += 1) {
    const r = rows[i];
    if (!r) continue;
    const iso = cellToISO(r[dateCol], inWindow);
    const login = up(r[driverCol]);
    if (!iso || !login) continue;
    const t = num(r[totalCol]);
    if (inWindow && !inWindow(iso)) { droppedRows += 1; droppedParcels += t; continue; }
    const key = `${iso}|${login}`;
    parcels[key] = (parcels[key] || 0) + t;
  }
  if (droppedRows) warnings.push(`Data tab: dropped ${droppedRows} row(s) (${droppedParcels} parcels) dated outside this file's period — backdated adjustments/corrections.`);
  return { parcels, droppedRows, droppedParcels };
}

// ── Activity Summary tab → hours per driver × day ────────────────────────────
function parseActivityTab(wb, warnings) {
  const empty = { hours: {}, minDate: null, maxDate: null };
  const name = findSheet(wb, [/activity\s*summar/i, /driver.*activity/i]);
  if (!name) return empty;
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, blankrows: false, defval: '' });
  if (!rows.length) return empty;
  const header = rows[0];
  const loginCol = (() => { const c = colByHeader(header, /logon\s*id|user\s*logon/i); return c >= 0 ? c : 4; })();
  const dateCol = (() => { const c = colByHeader(header, /event\s*date/i); return c >= 0 ? c : 5; })();
  const wccCol = (() => { const c = colByHeader(header, /wcc\s*name/i); return c >= 0 ? c : 2; })();
  const onbCol = (() => { const c = colByHeader(header, /onboard\s*items/i); return c >= 0 ? c : 22; })();
  const delCol = (() => { const c = colByHeader(header, /delivered\s*items/i); return c >= 0 ? c : 28; })();
  // The hours bases sit immediately LEFT of each "Parcels Per hour" column. Newer
  // files have TWO (Portal Time then Scanner Time); older files have ONE (a single
  // "Total time"), in which case there is no separate Scanner basis (col = -1 → 0).
  // Anchoring off PPH (not the duplicated "Total time" header) keeps it unambiguous.
  const pphCols = colsByHeader(header, /parcels?\s*per\s*hour/i);
  const portalHrsCol = pphCols.length >= 1 ? pphCols[0] - 1
    : (() => { const c = colByHeader(header, /total\s+time/i); return c >= 0 ? c : 12; })();
  const scannerHrsCol = pphCols.length >= 2 ? pphCols[1] - 1 : -1;
  const at = (r, c) => (c >= 0 ? r[c] : 0);

  // Older templates leave "Event Date" as "*" and carry the real date in
  // "Max. Event Local Date" — fall back to it when the primary date is missing.
  const maxEvtCol = colByHeader(header, /max\.?\s*event\s*local\s*date/i);

  const out = {}; // key → {portalHours, scannerHours, onboard, delivered, wcc}
  let minDate = null; let maxDate = null;
  for (let i = 1; i < rows.length; i += 1) {
    const r = rows[i];
    if (!r) continue;
    const login = up(r[loginCol]);
    if (!login || login === 'TOTAL' || up(r[0]) === 'GRAND TOTAL') continue;
    let iso = cellToISO(r[dateCol]);
    if (!iso && maxEvtCol >= 0) iso = cellToISO(r[maxEvtCol]);
    if (!iso) continue;
    if (!minDate || iso < minDate) minDate = iso;
    if (!maxDate || iso > maxDate) maxDate = iso;
    const key = `${iso}|${login}`;
    // A driver can appear once per day; if a file duplicates, keep the max hours.
    const prev = out[key] || { portalHours: 0, scannerHours: 0, onboard: 0, delivered: 0, wcc: '' };
    out[key] = {
      portalHours: Math.max(prev.portalHours, num(at(r, portalHrsCol))),
      scannerHours: Math.max(prev.scannerHours, num(at(r, scannerHrsCol))),
      onboard: Math.max(prev.onboard, num(at(r, onbCol))),
      delivered: Math.max(prev.delivered, num(at(r, delCol))),
      wcc: (r[wccCol] ? String(r[wccCol]).trim() : prev.wcc),
    };
  }
  return { hours: out, minDate, maxDate };
}

// ── Productivity tab → hours per driver × day (older files with no Activity tab)
// Header: Evt Local Date | Device Userid | … | Parcel Rate Delivered (per hour) |
// Actual Parcel Volume | Derived Hours Worked. One row per driver × day.
function parseProductivityTab(wb) {
  const empty = { hours: {}, minDate: null, maxDate: null };
  const name = findSheet(wb, [/productivit/i]);
  if (!name) return empty;
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, blankrows: false, defval: '' });
  if (!rows.length) return empty;
  const header = rows[0];
  const dateCol = (() => { const c = colByHeader(header, /evt\s*local\s*date|event\s*date/i); return c >= 0 ? c : 0; })();
  const loginCol = (() => { const c = colByHeader(header, /device\s*userid|logon/i); return c >= 0 ? c : 1; })();
  const hrsCol = (() => { const c = colByHeader(header, /derived\s*hours/i); return c >= 0 ? c : 8; })();
  const volCol = (() => { const c = colByHeader(header, /actual\s*parcel\s*volume/i); return c >= 0 ? c : 7; })();
  const out = {}; let minDate = null; let maxDate = null;
  for (let i = 1; i < rows.length; i += 1) {
    const r = rows[i];
    if (!r) continue;
    const login = up(r[loginCol]);
    if (!login || login === 'TOTAL' || /total/i.test(String(r[0]))) continue;
    const iso = cellToISO(r[dateCol]);
    if (!iso) continue;
    if (!minDate || iso < minDate) minDate = iso;
    if (!maxDate || iso > maxDate) maxDate = iso;
    const key = `${iso}|${login}`;
    const prev = out[key] || { portalHours: 0, scannerHours: 0, onboard: 0, delivered: 0, wcc: '' };
    out[key] = {
      portalHours: Math.max(prev.portalHours, num(r[hrsCol])),
      scannerHours: 0, // older files expose a single derived-hours figure only
      onboard: Math.max(prev.onboard, num(r[volCol])),
      delivered: prev.delivered,
      wcc: prev.wcc,
    };
  }
  return { hours: out, minDate, maxDate };
}

// Parse a WAHR monthly workbook into normalized per-driver-per-day rows.
// `arrayBuffer` is the raw file; `sourceFile` is the filename (provenance).
// Returns { rows, meta, warnings }. `rows` are ready to upsert into wahr_history.
export function parseWahrWorkbook(arrayBuffer, sourceFile = '') {
  const warnings = [];
  const wb = XLSX.read(arrayBuffer, { type: 'array', raw: true });

  // Period window: prefer the hand-maintained Totals tab (Paul's authoritative
  // daily totals define the file's true dates); fall back to the densest ≤6-week
  // cluster of Data volume when there's no usable Totals tab.
  const totals = parseTotalsTab(wb);
  let win = null;
  if (totals && totals.minDate) win = { lo: addISO(totals.minDate, -2), hi: addISO(totals.maxDate, 2) };
  else win = densestWindow(extractDataDates(wb));
  const inWindow = win ? (iso) => iso >= win.lo && iso <= win.hi : null;

  // Hours: newer files use the Activity Summary; older files (no Activity tab)
  // fall back to the Productivity tab. Either way, only hours rows inside the
  // period count (a stray-dated summary row must not add a phantom driver-day).
  let hoursSrc = 'Activity Summary';
  let { hours: hoursByKey } = parseActivityTab(wb, warnings);
  if (!Object.keys(hoursByKey).length) {
    const prod = parseProductivityTab(wb);
    if (Object.keys(prod.hours).length) { hoursByKey = prod.hours; hoursSrc = 'Productivity'; }
    else { hoursSrc = 'none'; warnings.push('No Activity Summary or Productivity tab — hours will be blank.'); }
  }
  if (inWindow) {
    for (const k of Object.keys(hoursByKey)) { if (!inWindow(k.split('|')[0])) delete hoursByKey[k]; }
  }
  const { parcels: parcelsByKey, droppedRows, droppedParcels } = parseDataTab(wb, warnings, inWindow);

  // Union of every (day, driver) seen in either tab.
  const keys = new Set([...Object.keys(parcelsByKey), ...Object.keys(hoursByKey)]);
  const rows = [];
  let minDate = null;
  let maxDate = null;
  const logins = new Set();
  for (const key of keys) {
    const [iso, login] = key.split('|');
    const h = hoursByKey[key] || {};
    const parcels = Math.round(parcelsByKey[key] || 0);
    rows.push({
      activity_date: iso,
      driver_login: login,
      raw_login: login,
      parcels,
      portal_hours: h.portalHours || 0,
      scanner_hours: h.scannerHours || 0,
      onboard_items: Math.round(h.onboard || 0),
      delivered_items: Math.round(h.delivered || 0),
      workcentre: h.wcc || '',
      source_file: sourceFile,
    });
    logins.add(login);
    if (!minDate || iso < minDate) minDate = iso;
    if (!maxDate || iso > maxDate) maxDate = iso;
  }
  rows.sort((a, b) => (a.activity_date < b.activity_date ? -1
    : a.activity_date > b.activity_date ? 1
      : a.driver_login.localeCompare(b.driver_login)));

  const totalParcels = rows.reduce((s, r) => s + r.parcels, 0);
  const totalOnboard = rows.reduce((s, r) => s + r.onboard_items, 0);
  const rowsWithHours = rows.filter((r) => r.portal_hours > 0).length;
  return {
    rows,
    warnings,
    meta: {
      sourceFile,
      rowCount: rows.length,
      driverCount: logins.size,
      logins: [...logins].sort(),
      minDate,
      maxDate,
      totalParcels,
      totalOnboard,
      hoursSrc,
      rowsWithHours,
      droppedRows,
      droppedParcels,
      totalsGrand: totals ? totals.grand : null,
      totalsSpan: totals ? `${totals.minDate}→${totals.maxDate}` : null,
    },
  };
}
